// Reading a church's schedule: the next Mass, whether there is one on a
// given day after a given hour, whether confession or adoration is in
// progress. All pure; the day and clock come in as arguments so this can be
// tested and so the LA map runs on LA time wherever the reader sits.
//
// A schedule row is [day, startMinutes, languageIndex] for Mass and
// [day, startMinutes, endMinutes] for confession and adoration. Day 0 is Sunday.

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The day and minute of the clock in a time zone, without a library. */
export function localClock(tz, date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', hour: 'numeric', minute: 'numeric', hour12: false }).formatToParts(date);
    const get = (t) => (parts.find((p) => p.type === t) || {}).value;
    const day = DAY_SHORT.indexOf(get('weekday'));
    const hour = Number(get('hour')) % 24;
    const minute = Number(get('minute'));
    if (day >= 0 && Number.isFinite(hour) && Number.isFinite(minute)) return { day, minutes: hour * 60 + minute, tz: true };
  } catch (_) { /* fall through to the reader's clock */ }
  return { day: date.getDay(), minutes: date.getHours() * 60 + date.getMinutes(), tz: false };
}

export function formatMinutes(m, lang = 'en') {
  const h24 = Math.floor(m / 60) % 24, min = m % 60;
  if (lang === 'es') return `${String(h24).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
  const h12 = h24 % 12 || 12;
  const suffix = h24 < 12 ? 'am' : 'pm';
  return min === 0 ? `${h12}${suffix}` : `${h12}:${String(min).padStart(2, '0')}${suffix}`;
}

/** Masses on `day` starting at or after `after` minutes (-1 = all day). */
export function massesOn(ms, day, after = -1) {
  if (!ms) return [];
  return ms.filter((r) => r[0] === day && (after < 0 || r[1] >= after));
}

/**
 * The next Mass from a clock, looking up to seven days ahead.
 * Returns { day, minutes, lang, inMinutes } or null.
 */
export function nextMass(ms, clock, langIndex) {
  if (!ms || !ms.length) return null;
  for (let ahead = 0; ahead < 8; ahead++) {
    const day = (clock.day + ahead) % 7;
    const floor = ahead === 0 ? clock.minutes : -1;
    const rows = massesOn(ms, day, floor);
    if (rows.length) {
      const r = rows.reduce((first, row) => row[1] < first[1] ? row : first);
      return { day, ahead, minutes: r[1], lang: langIndex ? langIndex[r[2]] : undefined, inMinutes: ahead * 1440 + r[1] - clock.minutes };
    }
  }
  return null;
}

/** True when a Mass starts within `window` minutes of the clock (today only, wrapping past midnight). */
export function massSoon(ms, clock, window = 120, lang = '') {
  if (!ms) return false;
  const idx = Number.isInteger(lang) ? lang : null;
  return ms.some((r) => {
    if (idx !== null && r[2] !== idx) return false;
    if (r[0] === clock.day) return r[1] >= clock.minutes && r[1] - clock.minutes <= window;
    if (r[0] === (clock.day + 1) % 7) return r[1] + 1440 - clock.minutes <= window;
    return false;
  });
}

/** True when a [day,start,end] window is open at the clock. */
export function openNow(rows, clock) {
  if (!rows) return false;
  return rows.some((r) => r[0] === clock.day && r[1] <= clock.minutes && clock.minutes < r[2]);
}

/** The next [day,start,end] window at or after the clock, or null. */
export function nextWindow(rows, clock) {
  if (!rows || !rows.length) return null;
  for (let ahead = 0; ahead < 8; ahead++) {
    const day = (clock.day + ahead) % 7;
    const hit = rows.filter((r) => r[0] === day && (ahead > 0 || r[2] > clock.minutes)).sort((a, b) => a[1] - b[1])[0];
    if (hit) return { day, start: hit[1], end: hit[2], ahead };
  }
  return null;
}

/** "Sunday 7:30am, 9:30am, 11:30am" grouped by day for a compact week readout. */
export function weekReadout(ms, langIndex, lang = 'en', names = DAY_NAMES) {
  if (!ms || !ms.length) return [];
  const byDay = new Map();
  for (const r of ms) {
    if (!byDay.has(r[0])) byDay.set(r[0], []);
    const code = langIndex ? langIndex[r[2]] : null;
    byDay.get(r[0]).push({ minutes: r[1], code });
  }
  const order = [0, 6, 1, 2, 3, 4, 5];  // Sunday, then the vigil day, then the week
  return order.filter((d) => byDay.has(d)).map((d) => ({
    day: d, name: names[d],
    times: byDay.get(d).sort((a, b) => a.minutes - b.minutes).map((t) => ({ label: formatMinutes(t.minutes, lang), code: t.code })),
  }));
}

/** Relative wording for "in 40 min", "tomorrow 7:30am", "Sunday 9am". */
export function describeNext(next, lang = 'en', names = DAY_NAMES) {
  if (!next) return '';
  const t = formatMinutes(next.minutes, lang);
  if (next.inMinutes <= 90) return lang === 'es' ? `en ${next.inMinutes} min` : `in ${next.inMinutes} min`;
  if (next.ahead === 0) return lang === 'es' ? `hoy ${t}` : `today ${t}`;
  if (next.ahead === 1) return lang === 'es' ? `mañana ${t}` : `tomorrow ${t}`;
  return `${names[next.day]} ${t}`;
}
